import { useQueryClient } from '@tanstack/react-query'
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { calculateDependentCoveragePortionOfEmployeeMonthlyAllowance } from 'utils/quote_calculations'
import { getGetEmployersRenewalsRenewalConfigsIdQueryKey as getEmployerRenewalConfigQueryKey, usePostQuotingProspectsProspectIdQuotesQuoteIdAllowances as useUpdateAllowancesMutation } from 'utils/web_api'
import { type EmployersRenewalsRenewalConfig, type PostQuotingProspectsProspectIdQuotesQuoteIdAllowancesAllowancesItem as UpdateAllowanceItem, type QuotingQuoteAllowance } from 'utils/web_api.schemas'

import { useEmployerRenewalConfig } from './EmployerRenewalConfigProvider'
import { convertAllowancesToAllowanceParams, getDynamicAllowance, renewalApproachFromAllowance } from '../pages/allowances/utils'
import { type EmployeeFilter, type FlatAllowanceApproach, type RenewalApproach } from '../types'
import { useQuoteResults } from './QuoteResultsProvider'
import { RENEWAL_EVENTS, useEmitEmployerRenewalsEvent } from '../events'
import useFlatAllowanceRecommendedAllowances from '../hooks/useFlatAllowanceRecommendedAllowances'
import { minContributionsForQuoteResults } from '../utils/flatAllowanceRecommendations'

type AllowancesContextType = {
  currentYearAllowances: QuotingQuoteAllowance[]
  renewalYearAllowances: QuotingQuoteAllowance[]
  renewalYearAllowanceParams: UpdateAllowanceItem[]
  setRenewalYearAllowanceParams: (allowances: UpdateAllowanceItem[]) => void
  recommendedFlatAllowances: QuotingQuoteAllowance[]
  renewalApproach: RenewalApproach
  setRenewalApproach: (approach: RenewalApproach) => void
  flatAllowanceApproach?: FlatAllowanceApproach
  setFlatAllowanceApproach: (approach: FlatAllowanceApproach) => void
  saveAllowances: () => Promise<boolean>
  isSavingAllowances?: boolean
  calculateAdditionalDependentCoverageCosts: (spousePercentage: number, additionalDependentPercentage: number) => number
  minimumContributions?: {
    minContributionByState: Record<string, number>
    minDefaultContribution: number
  }
  employerOfferedQmeInCurrentYear: boolean
  employerWillOfferQmeInRenewalYear: boolean
  setEmployerWillOfferQmeInRenewalYear: (willOfferQmeInRenewalYear: boolean) => void
  employerOfferedDependentCoverageInCurrentYear: boolean
  setRenewalYearAllowances: (updateAllowance: QuotingQuoteAllowance | QuotingQuoteAllowance[]) => void
  employeeFilter: EmployeeFilter
  setEmployeeFilter: (employeeFilter: EmployeeFilter) => void
}

const AllowancesContext = createContext<AllowancesContextType>({
  currentYearAllowances: [],
  renewalYearAllowances: [],
  renewalYearAllowanceParams: [],
  setRenewalYearAllowanceParams: () => {},
  recommendedFlatAllowances: [],
  renewalApproach: 'match',
  setRenewalApproach: () => {},
  setFlatAllowanceApproach: () => {},
  saveAllowances: () => Promise.resolve(false),
  employerOfferedQmeInCurrentYear: false,
  employerWillOfferQmeInRenewalYear: false,
  setEmployerWillOfferQmeInRenewalYear: () => {},
  calculateAdditionalDependentCoverageCosts: () => 0,
  employerOfferedDependentCoverageInCurrentYear: false,
  setRenewalYearAllowances: () => {},
  employeeFilter: 'enrolled',
  setEmployeeFilter: () => {},
})

export function useAllowances () {
  return useContext(AllowancesContext)
}

export function AllowancesProvider ({ children }: PropsWithChildren) {
  const { employerRenewalConfig, renewalConfigId, isUsingFlatAllowances } = useEmployerRenewalConfig()
  const { quoteResults } = useQuoteResults()
  const { recommendedAllowances: recommendedFlatAllowances, isUsingCurrentAllowances, isUsingRecommendedAllowances, isLoading: isLoadingRecommendedAllowances } = useFlatAllowanceRecommendedAllowances()
  const { emitEvent } = useEmitEmployerRenewalsEvent()

  const [renewalApproach, setRenewalApproach] = useState<RenewalApproach>()
  const [flatAllowanceApproach, setFlatAllowanceApproach] = useState<FlatAllowanceApproach>()
  const [renewalYearAllowanceParams, setRenewalYearAllowanceParams] = useState<UpdateAllowanceItem[]>([])
  const [employeeFilter, setEmployeeFilter] = useState<EmployeeFilter>('enrolled')

  const currentYearAllowances = employerRenewalConfig?.current_year_quote.allowances
  const renewalYearAllowances = employerRenewalConfig?.renewal_year_quote.allowances
  const renewalYearQuoteResults = quoteResults?.matched.quote_results

  const renewalConfigQueryKey = getEmployerRenewalConfigQueryKey(renewalConfigId)

  const setEmployerWillOfferQmeInRenewalYear = useCallback((willOfferQmeInRenewalYear: boolean) => {
    setRenewalYearAllowanceParams(renewalYearAllowanceParams.map(allowance => ({
      ...allowance,
      qme_treatment: willOfferQmeInRenewalYear ? 'all_allowed' : 'disabled',
    })))

    if (willOfferQmeInRenewalYear) {
      emitEvent(RENEWAL_EVENTS.QME_TOGGLED_ON)
    } else {
      emitEvent(RENEWAL_EVENTS.QME_TOGGLED_OFF)
    }
  }, [renewalYearAllowanceParams])

  const employerWillOfferQmeInRenewalYear = useMemo(() => {
    // assume all allowance params have the same qme treatment
    return renewalYearAllowanceParams.some(allowance =>
      allowance.qme_treatment === 'all_allowed' || allowance.qme_treatment === 'copay_only'
    ) ?? false
  }, [renewalYearAllowanceParams])

  const employerOfferedQmeInCurrentYear = useMemo(() => {
    return employerRenewalConfig?.current_year_quote.allowances?.some(allowance =>
      allowance.qme_treatment === 'all_allowed' || allowance.qme_treatment === 'copay_only'
    ) ?? false
  }, [employerRenewalConfig?.current_year_quote?.allowances])

  useEffect(() => {
    setRenewalYearAllowanceParams(convertAllowancesToAllowanceParams(employerRenewalConfig?.renewal_year_quote.allowances ?? []))
  }, [employerRenewalConfig?.renewal_year_quote.allowances])

  const minimumContributions = useMemo(() => {
    if (!isUsingFlatAllowances || !renewalYearQuoteResults || !currentYearAllowances) {
      return
    }

    return minContributionsForQuoteResults(renewalYearQuoteResults, currentYearAllowances)
  }, [renewalYearQuoteResults, currentYearAllowances])

  // Set default renewal year allowances for dynamic allowances
  useEffect(() => {
    if (isUsingFlatAllowances || !renewalYearAllowances) {
      return
    }

    setRenewalYearAllowanceParams(convertAllowancesToAllowanceParams(renewalYearAllowances))
  }, [renewalYearAllowances, isUsingFlatAllowances])

  // Set default flat allowance approach based on saved allowances
  useEffect(() => {
    if (!isUsingFlatAllowances || flatAllowanceApproach || !quoteResults || isLoadingRecommendedAllowances) {
      return
    }

    let updatedFlatAllowanceApproach: FlatAllowanceApproach | undefined

    if (isUsingCurrentAllowances) {
      updatedFlatAllowanceApproach = 'current'
    } else if (isUsingRecommendedAllowances) {
      updatedFlatAllowanceApproach = 'recommended'
    } else {
      updatedFlatAllowanceApproach = 'custom'
    }

    if (updatedFlatAllowanceApproach) {
      setFlatAllowanceApproachInternal(updatedFlatAllowanceApproach)
    }
  }, [quoteResults, isLoadingRecommendedAllowances, isUsingCurrentAllowances, isUsingRecommendedAllowances])

  // Set default dynamic allowance approach based on saved allowances
  useEffect(() => {
    if (renewalApproach || !quoteResults || !employerRenewalConfig || isUsingFlatAllowances) {
      return
    }

    const dynamicAllowance = getDynamicAllowance(renewalYearAllowances ?? [])

    const updatedRenewalApproach = renewalApproachFromAllowance(dynamicAllowance, quoteResults)

    if (updatedRenewalApproach) {
      setRenewalApproach(updatedRenewalApproach)
    }
  }, [quoteResults, employerRenewalConfig])

  const queryClient = useQueryClient()
  const updateAllowancesMutation = useUpdateAllowancesMutation({
    mutation: {
      onSuccess: newAllowances => setRenewalYearAllowances(newAllowances),
    },
  })

  const setRenewalYearAllowances = useCallback((updatedAllowance: QuotingQuoteAllowance | QuotingQuoteAllowance[]) => {
    queryClient.setQueryData(renewalConfigQueryKey, (oldData: EmployersRenewalsRenewalConfig) => {
      return {
        ...oldData,
        renewal_year_quote: {
          ...oldData.renewal_year_quote,
          allowances: Array.isArray(updatedAllowance)
            ? updatedAllowance
            : oldData.renewal_year_quote.allowances?.map(existingAllowance => {
              return updatedAllowance.id === existingAllowance.id ? updatedAllowance : existingAllowance
            }),
        },
      }
    })
  }, [renewalConfigQueryKey, queryClient])

  const setRenewalApproachInternal = useCallback((approach: RenewalApproach) => {
    setRenewalApproach(approach)

    if (!quoteResults) {
      return
    }

    // Update the benchmark in the renewal year allowances
    let renewalBenchmark = quoteResults.matched.benchmark

    if (approach === 'match') {
      emitEvent(RENEWAL_EVENTS.SMART_ALLOWANCE_APPROACH_MATCH_CLICKED)
    }

    if (approach === 'reduce') {
      renewalBenchmark = quoteResults.reduced?.benchmark

      emitEvent(RENEWAL_EVENTS.SMART_ALLOWANCE_APPROACH_REDUCE_CLICKED)
    }

    if (approach === 'upgrade') {
      renewalBenchmark = quoteResults.upgraded?.benchmark

      emitEvent(RENEWAL_EVENTS.SMART_ALLOWANCE_APPROACH_UPGRADE_CLICKED)
    }

    let confirmedBenchmark: string

    if (renewalBenchmark?.metal_tier === 'all_plans_silver_plus') {
      confirmedBenchmark = 'all_plans_silver_plus'
    } else {
      confirmedBenchmark = `on_thatch_${renewalBenchmark?.metal_tier}`
    }

    if (renewalBenchmark) {
      queryClient.setQueryData(renewalConfigQueryKey, (oldData: EmployersRenewalsRenewalConfig) => {
        return {
          ...oldData,
          renewal_year_quote: {
            ...oldData.renewal_year_quote,
            allowances: oldData.renewal_year_quote.allowances?.map(allowance => {
              return {
                ...allowance,
                benchmark: confirmedBenchmark,
                coverage_percentile: renewalBenchmark.coverage_percentile,
              }
            }),
          }
        }
      })
    }
  }, [setRenewalApproach, quoteResults, renewalConfigId, queryClient])

  const setRenewalYearAllowanceParamsInternal = useCallback((allowances: UpdateAllowanceItem[]) => {
    setRenewalYearAllowanceParams(allowances)

    if (isUsingFlatAllowances) {
      setFlatAllowanceApproach('custom')
    }
  }, [isUsingFlatAllowances])

  const setFlatAllowanceApproachInternal = useCallback((approach: FlatAllowanceApproach) => {
    setFlatAllowanceApproach(approach)

    if (approach === 'recommended') {
      setRenewalYearAllowanceParams(convertAllowancesToAllowanceParams(recommendedFlatAllowances))
    } else if (approach === 'current' && currentYearAllowances) {
      setRenewalYearAllowanceParams(convertAllowancesToAllowanceParams(currentYearAllowances))
    } else if (approach === 'custom' && renewalYearAllowances) {
      setRenewalYearAllowanceParams(convertAllowancesToAllowanceParams(renewalYearAllowances))
    }

    emitEvent(RENEWAL_EVENTS.FLAT_ALLOWANCE_APPROACH_CHANGED, {
      approach,
    })
  }, [recommendedFlatAllowances, currentYearAllowances, renewalYearAllowances])

  const calculateAdditionalDependentCoverageCosts = useCallback((spousePercentage: number, additionalDependentPercentage: number) => {
    return renewalYearQuoteResults?.reduce((acc, curr) => acc + calculateDependentCoveragePortionOfEmployeeMonthlyAllowance(curr, spousePercentage / 100, additionalDependentPercentage / 100), 0) ?? 0
  }, [renewalYearQuoteResults])

  const saveAllowances = useCallback(async () => {
    try {
      await updateAllowancesMutation.mutateAsync({
        prospectId: employerRenewalConfig?.prospect_id ?? '',
        quoteId: employerRenewalConfig?.renewal_year_quote.id ?? '',
        data: {
          allowances: renewalYearAllowanceParams,
        },
      })

      return true
    } catch (error) {
      console.error('Failed to save allowances:', error)
      return false
    }
  }, [updateAllowancesMutation, renewalYearAllowanceParams, employerRenewalConfig])

  const employerOfferedDependentCoverageInCurrentYear = useMemo(() => {
    // if any allowance has a spouse or dependent contribution percentage or amount, then we consider the employer to have offered dependent coverage
    return employerRenewalConfig?.current_year_quote?.allowances?.some(allowance => (
      (allowance.spouse_contribution_percentage || 0) > 0 ||
        (allowance.dependent_contribution_percentage || 0) > 0 ||
        (allowance.spouse_amount?.subunits || 0) > 0 ||
        (allowance.dependent_amount?.subunits || 0) > 0
    )) ?? false
  }, [employerRenewalConfig])

  return (
    <AllowancesContext.Provider value={{
      currentYearAllowances: currentYearAllowances ?? [],
      renewalYearAllowances: renewalYearAllowances ?? [],
      renewalYearAllowanceParams,
      setRenewalYearAllowanceParams: setRenewalYearAllowanceParamsInternal,
      setRenewalYearAllowances,
      recommendedFlatAllowances,
      renewalApproach: renewalApproach ?? 'match',
      setRenewalApproach: setRenewalApproachInternal,
      flatAllowanceApproach,
      setFlatAllowanceApproach: setFlatAllowanceApproachInternal,
      employerOfferedQmeInCurrentYear,
      saveAllowances,
      isSavingAllowances: updateAllowancesMutation.isPending,
      minimumContributions,
      employerWillOfferQmeInRenewalYear,
      setEmployerWillOfferQmeInRenewalYear,
      calculateAdditionalDependentCoverageCosts,
      employerOfferedDependentCoverageInCurrentYear,
      employeeFilter,
      setEmployeeFilter,
    }}
    >
      {children}
    </AllowancesContext.Provider>
  )
}
